/**
 * Time Estimation System for ADHD Users
 * 
 * Provides intelligent time estimation adjustments based on user history,
 * task complexity, energy levels, and selected estimation mode.
 * Addresses the common ADHD challenge of time blindness.
 */

import { logger } from './logger';

export type TimeEstimationMode = 'optimistic' | 'realistic' | 'pessimistic';

export interface TimeHistory {
  estimatedMinutes: number;
  actualMinutes: number;
  taskComplexity: number;
  energyLevel: number;
  date: Date;
  wasCompleted: boolean;
  distractionCount?: number;
}

export interface TaskEstimationInput {
  description: string;
  complexity: number; // 1-10
  energyLevel?: number; // 1-5
  userHistory?: TimeHistory[];
  mode: TimeEstimationMode;
  tags?: string[];
  timeOfDay?: 'morning' | 'afternoon' | 'evening';
}

export interface EstimationResult {
  originalEstimate: number;
  adjustedEstimate: number;
  confidenceLevel: number; // 0-1
  adjustmentFactors: {
    historyFactor: number;
    complexityFactor: number;
    energyFactor: number;
    timeOfDayFactor: number;
    modeFactor: number;
  };
  explanation: string;
  suggestedBreaks?: number[];
}

export class TimeEstimationEngine {
  private readonly DEFAULT_ESTIMATES: Record<number, number> = {
    1: 5,   // Very simple tasks: 5 minutes
    2: 10,  // Simple tasks: 10 minutes
    3: 20,  // Easy tasks: 20 minutes
    4: 30,  // Moderate tasks: 30 minutes
    5: 45,  // Medium tasks: 45 minutes
    6: 60,  // Complex tasks: 1 hour
    7: 90,  // Very complex tasks: 1.5 hours
    8: 120, // Hard tasks: 2 hours
    9: 180, // Very hard tasks: 3 hours
    10: 240 // Extremely complex tasks: 4 hours
  };

  private readonly TIME_OF_DAY_MULTIPLIERS = {
    morning: 0.9,   // Generally more focused in morning
    afternoon: 1.0, // Baseline
    evening: 1.2    // Often less focused in evening
  };

  private readonly MODE_MULTIPLIERS: Record<TimeEstimationMode, number> = {
    optimistic: 1.0,   // Use raw estimate (ADHD default tendency)
    realistic: 1.4,    // Add 40% buffer based on research
    pessimistic: 2.0   // Double estimate for high-stress situations
  };

  /**
   * Main estimation function
   */
  estimateTime(input: TaskEstimationInput): EstimationResult {
    const baseEstimate = this.getBaseEstimate(input.complexity, input.description);
    
    // Calculate adjustment factors
    const historyFactor = this.calculateHistoryFactor(input.userHistory, input.complexity);
    const complexityFactor = this.calculateComplexityFactor(input.complexity);
    const energyFactor = this.calculateEnergyFactor(input.energyLevel || 3);
    const timeOfDayFactor = this.TIME_OF_DAY_MULTIPLIERS[input.timeOfDay || 'afternoon'];
    const modeFactor = this.MODE_MULTIPLIERS[input.mode];

    // Apply adjustments
    let adjustedEstimate = baseEstimate;
    adjustedEstimate *= historyFactor;
    adjustedEstimate *= complexityFactor;
    adjustedEstimate *= energyFactor;
    adjustedEstimate *= timeOfDayFactor;
    adjustedEstimate *= modeFactor;

    // Round to reasonable increments
    adjustedEstimate = this.roundToReasonableIncrement(adjustedEstimate);

    // Calculate confidence level
    const confidenceLevel = this.calculateConfidence(input.userHistory, input.complexity);

    // Generate explanation
    const explanation = this.generateExplanation(input, {
      historyFactor,
      complexityFactor,
      energyFactor,
      timeOfDayFactor,
      modeFactor
    });

    // Suggest breaks for longer tasks
    const suggestedBreaks = this.suggestBreaks(adjustedEstimate);

    return {
      originalEstimate: baseEstimate,
      adjustedEstimate,
      confidenceLevel,
      adjustmentFactors: {
        historyFactor,
        complexityFactor,
        energyFactor,
        timeOfDayFactor,
        modeFactor
      },
      explanation,
      suggestedBreaks
    };
  }

  /**
   * Get base estimate from complexity and description analysis
   */
  private getBaseEstimate(complexity: number, description: string): number {
    let baseEstimate = this.DEFAULT_ESTIMATES[Math.round(complexity)] || 30;

    // Analyze description for keywords that might affect time
    const timeIndicators = this.analyzeDescriptionForTime(description);
    if (timeIndicators.multiplier !== 1.0) {
      baseEstimate *= timeIndicators.multiplier;
      logger.debug('Description analysis adjusted estimate', { 
        original: baseEstimate / timeIndicators.multiplier,
        adjusted: baseEstimate,
        reason: timeIndicators.reason
      });
    }

    return baseEstimate;
  }

  /**
   * Analyze task description for time-related keywords
   */
  private analyzeDescriptionForTime(description: string): { multiplier: number, reason: string } {
    const lowerDesc = description.toLowerCase();
    
    // Quick/fast tasks
    if (lowerDesc.includes('quick') || lowerDesc.includes('fast') || lowerDesc.includes('brief')) {
      return { multiplier: 0.7, reason: 'Quick task keywords detected' };
    }
    
    // Research/learning tasks
    if (lowerDesc.includes('research') || lowerDesc.includes('learn') || lowerDesc.includes('study')) {
      return { multiplier: 1.5, reason: 'Research/learning task detected' };
    }
    
    // Writing tasks
    if (lowerDesc.includes('write') || lowerDesc.includes('draft') || lowerDesc.includes('compose')) {
      return { multiplier: 1.3, reason: 'Writing task detected' };
    }
    
    // Meeting/call tasks
    if (lowerDesc.includes('meeting') || lowerDesc.includes('call') || lowerDesc.includes('discuss')) {
      return { multiplier: 1.2, reason: 'Meeting/discussion task detected' };
    }
    
    // Setup/installation tasks
    if (lowerDesc.includes('setup') || lowerDesc.includes('install') || lowerDesc.includes('configure')) {
      return { multiplier: 1.8, reason: 'Setup/configuration task detected' };
    }

    return { multiplier: 1.0, reason: 'No special indicators found' };
  }

  /**
   * Calculate history-based adjustment factor
   */
  private calculateHistoryFactor(history?: TimeHistory[], complexity?: number): number {
    if (!history || history.length === 0) {
      return 1.0; // No history, use default
    }

    // Filter history for similar complexity tasks (±2 points)
    const relevantHistory = history.filter(h => 
      complexity ? Math.abs(h.taskComplexity - complexity) <= 2 : true
    );

    if (relevantHistory.length === 0) {
      return 1.0;
    }

    // Calculate average accuracy ratio
    const accuracyRatios = relevantHistory.map(h => {
      if (h.estimatedMinutes === 0) return 1.0;
      return h.actualMinutes / h.estimatedMinutes;
    });

    const avgAccuracy = accuracyRatios.reduce((sum, ratio) => sum + ratio, 0) / accuracyRatios.length;

    // Weight more recent history higher
    const now = new Date();
    const weightedRatios = relevantHistory.map(h => {
      const daysSince = (now.getTime() - h.date.getTime()) / (1000 * 60 * 60 * 24);
      const weight = Math.exp(-daysSince / 30); // Exponential decay over 30 days
      return { ratio: h.actualMinutes / h.estimatedMinutes, weight };
    });

    const weightedSum = weightedRatios.reduce((sum, wr) => sum + (wr.ratio * wr.weight), 0);
    const totalWeight = weightedRatios.reduce((sum, wr) => sum + wr.weight, 0);

    const weightedAverage = totalWeight > 0 ? weightedSum / totalWeight : avgAccuracy;

    // Cap the adjustment to reasonable bounds
    return Math.max(0.5, Math.min(3.0, weightedAverage));
  }

  /**
   * Calculate complexity-based adjustment
   */
  private calculateComplexityFactor(complexity: number): number {
    // Higher complexity tasks tend to have more unknowns and take longer
    if (complexity >= 8) return 1.3; // Very complex tasks often take longer than expected
    if (complexity >= 6) return 1.1; // Moderately complex tasks
    if (complexity <= 2) return 0.9; // Simple tasks might be faster
    return 1.0; // Medium complexity is baseline
  }

  /**
   * Calculate energy-based adjustment
   */
  private calculateEnergyFactor(energyLevel: number): number {
    // Energy levels 1-5, where 5 is highest energy
    const energyMultipliers = {
      1: 1.8, // Very low energy = much slower
      2: 1.4, // Low energy = slower
      3: 1.0, // Medium energy = baseline
      4: 0.9, // High energy = slightly faster
      5: 0.8  // Very high energy = faster
    };
    
    return energyMultipliers[energyLevel as keyof typeof energyMultipliers] || 1.0;
  }

  /**
   * Calculate confidence based on history quality
   */
  private calculateConfidence(history?: TimeHistory[], complexity?: number): number {
    if (!history || history.length === 0) {
      return 0.3; // Low confidence without history
    }

    const relevantHistory = history.filter(h => 
      complexity ? Math.abs(h.taskComplexity - complexity) <= 2 : true
    );

    if (relevantHistory.length === 0) {
      return 0.4; // Slightly better with some history
    }

    // Base confidence on number of similar tasks and their consistency
    const baseConfidence = Math.min(0.9, 0.3 + (relevantHistory.length * 0.1));
    
    // Reduce confidence if estimates were very inaccurate
    const accuracyRatios = relevantHistory.map(h => h.actualMinutes / h.estimatedMinutes);
    const variance = this.calculateVariance(accuracyRatios);
    const consistencyFactor = Math.max(0.5, 1.0 - (variance * 0.5));

    return baseConfidence * consistencyFactor;
  }

  /**
   * Calculate variance of a set of numbers
   */
  private calculateVariance(numbers: number[]): number {
    if (numbers.length === 0) return 0;
    const mean = numbers.reduce((sum, n) => sum + n, 0) / numbers.length;
    const squareDiffs = numbers.map(n => Math.pow(n - mean, 2));
    return squareDiffs.reduce((sum, diff) => sum + diff, 0) / numbers.length;
  }

  /**
   * Round estimate to reasonable increments
   */
  private roundToReasonableIncrement(minutes: number): number {
    if (minutes <= 15) return Math.round(minutes / 5) * 5; // 5-minute increments
    if (minutes <= 60) return Math.round(minutes / 10) * 10; // 10-minute increments
    if (minutes <= 180) return Math.round(minutes / 15) * 15; // 15-minute increments
    return Math.round(minutes / 30) * 30; // 30-minute increments for long tasks
  }

  /**
   * Generate human-readable explanation
   */
  private generateExplanation(
    input: TaskEstimationInput, 
    factors: Record<string, number>
  ): string {
    const parts: string[] = [];
    
    parts.push(`Base estimate for complexity ${input.complexity}: ${this.DEFAULT_ESTIMATES[Math.round(input.complexity)]} minutes`);
    
    if (factors.historyFactor !== 1.0) {
      const direction = factors.historyFactor > 1.0 ? 'increased' : 'decreased';
      const percentage = Math.round(Math.abs(factors.historyFactor - 1.0) * 100);
      parts.push(`${direction} by ${percentage}% based on your completion history`);
    }
    
    if (factors.energyFactor !== 1.0) {
      const direction = factors.energyFactor > 1.0 ? 'increased' : 'decreased';
      parts.push(`${direction} due to current energy level`);
    }
    
    if (factors.timeOfDayFactor !== 1.0) {
      const direction = factors.timeOfDayFactor > 1.0 ? 'increased' : 'decreased';
      parts.push(`${direction} for ${input.timeOfDay} work`);
    }
    
    const modeDescriptions = {
      optimistic: 'using optimistic estimation (your initial estimate)',
      realistic: 'adding realistic buffer time (40% extra)',
      pessimistic: 'doubling estimate for safety buffer'
    };
    parts.push(modeDescriptions[input.mode]);
    
    return parts.join(', ');
  }

  /**
   * Suggest break points for longer tasks
   */
  private suggestBreaks(totalMinutes: number): number[] {
    if (totalMinutes <= 30) return []; // No breaks needed for short tasks
    
    const breaks: number[] = [];
    let currentTime = 0;
    
    // Suggest breaks every 25-50 minutes depending on task length
    const breakInterval = totalMinutes > 120 ? 45 : 25;
    
    while (currentTime + breakInterval < totalMinutes) {
      currentTime += breakInterval;
      breaks.push(currentTime);
    }
    
    return breaks;
  }

  /**
   * Track actual completion time for learning
   */
  recordCompletion(
    estimatedMinutes: number,
    actualMinutes: number,
    taskComplexity: number,
    energyLevel: number,
    wasCompleted: boolean,
    distractionCount?: number
  ): TimeHistory {
    const entry: TimeHistory = {
      estimatedMinutes,
      actualMinutes,
      taskComplexity,
      energyLevel,
      date: new Date(),
      wasCompleted,
      distractionCount
    };

    // Store in localStorage for persistence
    try {
      const existingHistory = this.getStoredHistory();
      const updatedHistory = [...existingHistory, entry];
      
      // Keep only last 100 entries to prevent storage bloat
      const trimmedHistory = updatedHistory.slice(-100);
      
      localStorage.setItem('time_estimation_history', JSON.stringify(trimmedHistory));
      logger.info('Time estimation history updated', { entry });
    } catch (error) {
      logger.error('Failed to save time estimation history', error);
    }

    return entry;
  }

  /**
   * Get stored history from localStorage
   */
  getStoredHistory(): TimeHistory[] {
    try {
      const stored = localStorage.getItem('time_estimation_history');
      if (!stored) return [];
      
      const history = JSON.parse(stored);
      // Convert date strings back to Date objects
      return history.map((entry: any) => ({
        ...entry,
        date: new Date(entry.date)
      }));
    } catch (error) {
      logger.error('Failed to load time estimation history', error);
      return [];
    }
  }

  /**
   * Get accuracy statistics for user feedback
   */
  getAccuracyStats(): {
    totalTasks: number;
    averageAccuracy: number;
    overestimatePercentage: number;
    underestimatePercentage: number;
    improvementTrend: 'improving' | 'declining' | 'stable';
  } {
    const history = this.getStoredHistory();
    
    if (history.length === 0) {
      return {
        totalTasks: 0,
        averageAccuracy: 0,
        overestimatePercentage: 0,
        underestimatePercentage: 0,
        improvementTrend: 'stable'
      };
    }

    const accuracyRatios = history.map(h => h.actualMinutes / h.estimatedMinutes);
    const averageAccuracy = accuracyRatios.reduce((sum, ratio) => sum + ratio, 0) / accuracyRatios.length;
    
    const overestimates = accuracyRatios.filter(ratio => ratio < 0.8).length;
    const underestimates = accuracyRatios.filter(ratio => ratio > 1.2).length;
    
    // Calculate trend from recent vs older data
    const recentHistory = history.slice(-20);
    const olderHistory = history.slice(0, -20);
    
    let improvementTrend: 'improving' | 'declining' | 'stable' = 'stable';
    if (recentHistory.length >= 5 && olderHistory.length >= 5) {
      const recentAccuracy = recentHistory.reduce((sum, h) => sum + (h.actualMinutes / h.estimatedMinutes), 0) / recentHistory.length;
      const olderAccuracy = olderHistory.reduce((sum, h) => sum + (h.actualMinutes / h.estimatedMinutes), 0) / olderHistory.length;
      
      if (Math.abs(recentAccuracy - 1.0) < Math.abs(olderAccuracy - 1.0)) {
        improvementTrend = 'improving';
      } else if (Math.abs(recentAccuracy - 1.0) > Math.abs(olderAccuracy - 1.0)) {
        improvementTrend = 'declining';
      }
    }

    return {
      totalTasks: history.length,
      averageAccuracy,
      overestimatePercentage: (overestimates / history.length) * 100,
      underestimatePercentage: (underestimates / history.length) * 100,
      improvementTrend
    };
  }
}

// Export singleton instance
export const timeEstimationEngine = new TimeEstimationEngine();